import { nanoid } from "nanoid"
import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  offset,
  arrow,
} from "@floating-ui/dom"

const CONTAINER_DOM_ID = `tooltip-container`
const SHIFT_PADDING = 5

class Tooltip {
  constructor(targetNode) {
    this.targetNode = targetNode
    this.container = document.getElementById(CONTAINER_DOM_ID)
    this.tooltipNode = null
    this.cleanupFloater = null
    this.targetDefaultTitle = ``
    this.targetDefaultDescribedBy = ``
    this.show = this.show.bind(this)
    this.hide = this.hide.bind(this)
  }

  init() {
    const { tooltipInited } = this.targetNode.dataset

    if (
      tooltipInited ||
      !this.tooltipText ||
      !this.container ||
      !window.liveSocket
    )
      return

    window.liveSocket
      .js()
      .setAttribute(this.targetNode, `data-tooltip-inited`, true)

    if (this.tooltipShow) {
      this.show()
      this.tooltipNode?.addEventListener(`click`, this.hide)
      this.targetNode.addEventListener(`click`, this.hide)
    } else {
      this.targetNode.addEventListener(`mouseenter`, this.show)
      this.targetNode.addEventListener(`focus`, this.show)
      this.targetNode.addEventListener(`mouseleave`, this.hide)
      this.targetNode.addEventListener(`blur`, this.hide)
    }
  }

  get tooltipShow() {
    return this.targetNode.dataset.tooltipShow == `true`
  }

  get tooltipText() {
    return (
      this.targetNode.dataset.tooltipText ||
      this.targetNode.title ||
      this.targetNode.ariaLabel ||
      ``
    ).trim()
  }

  show() {
    if (this.tooltipNode || !this.tooltipText) return

    this.tooltipNode = document.createElement(`div`)
    this.tooltipNode.className = `tooltip`
    this.tooltipNode.role = `tooltip`
    this.tooltipNode.id = `a${nanoid()}`
    this.tooltipNode.innerHTML = `<div class="tooltip--text">${this.tooltipText}</div>`
    this.tooltipNode.style.setProperty(
      `--tooltip-component-shift-padding`,
      `${SHIFT_PADDING}px`
    )
    this.container.appendChild(this.tooltipNode)

    const arrowNode = document.createElement(`div`)
    arrowNode.className = `tooltip--arrow`
    this.tooltipNode.appendChild(arrowNode)

    const arrowSize = window
      .getComputedStyle(arrowNode)
      .getPropertyValue(`--tooltip-component-arrow-size`)

    if (window.liveSocket) {
      this.targetDefaultTitle = this.targetNode.title
      this.targetDefaultDescribedBy =
        this.targetNode.getAttribute(`aria-describedby`)

      window.liveSocket.js().removeAttribute(this.targetNode, `title`)
      window.liveSocket
        .js()
        .setAttribute(this.targetNode, `aria-describedby`, this.tooltipNode.id)
    }

    this.cleanupFloater = autoUpdate(this.targetNode, this.tooltipNode, () =>
      computePosition(this.targetNode, this.tooltipNode, {
        placement: this.targetNode.dataset.tooltipPlacement || `top`,
        middleware: [
          flip(),
          offset(20),
          shift({ padding: SHIFT_PADDING }),
          arrow({ element: arrowNode }),
        ],
      }).then(({ x, y, placement, middlewareData }) => {
        Object.assign(this.tooltipNode.style, {
          top: `${y}px`,
          left: `${x}px`,
          display: `block`,
        })

        const { x: arrowX, y: arrowY } = middlewareData.arrow

        const arrowSide = {
          top: `bottom`,
          right: `left`,
          bottom: `top`,
          left: `right`,
        }[placement.split(`-`)[0]]

        let arrowRotate = `0deg`
        switch (arrowSide) {
          case `left`:
            arrowRotate = `-90deg`
            break
          case `right`:
            arrowRotate = `90deg`
            break
          case `top`:
            arrowRotate = `180deg`
            break
        }

        Object.assign(arrowNode.style, {
          left: arrowX != null ? `${arrowX}px` : ``,
          top: arrowY != null ? `${arrowY}px` : ``,
          right: ``,
          bottom: ``,
          rotate: arrowRotate,
          [arrowSide]: `calc((-1 * ${arrowSize}) + 1px)`,
        })
      })
    )
  }

  hide() {
    this.tooltipNode?.remove()
    this.tooltipNode = null
    this.cleanupFloater?.()
    this.cleanupFloater = null

    if (this.tooltipShow)
      this.targetNode.removeEventListener(`click`, this.show)

    if (window.liveSocket) {
      if (this.targetDefaultDescribedBy) {
        window.liveSocket
          .js()
          .setAttribute(
            this.targetNode,
            `aria-describedby`,
            this.targetDefaultDescribedBy
          )

        this.targetDefaultDescribedBy = ``
      } else
        window.liveSocket
          .js()
          .removeAttribute(this.targetNode, `aria-describedby`)

      if (this.targetDefaultTitle) {
        window.liveSocket
          .js()
          .setAttribute(this.targetNode, `title`, this.targetDefaultTitle)

        this.targetDefaultTitle = ``
      }
    }
  }
}

export default function initTooltip() {
  if (!document.getElementById(CONTAINER_DOM_ID)) {
    console.warn(`Tooltip container not found, cannot initialize tooltips`)
    return
  }

  if (!window.liveSocket) {
    console.warn(`LiveSocket not found, cannot initialize tooltips`)
    return
  }

  // initial scan
  document.body
    .querySelectorAll?.(`[data-tooltip]`)
    .forEach((el) => new Tooltip(el).init())

  // observe for dom changes
  const observer = new MutationObserver((mutations) => {
    const targets = new Set()

    for (const mutation of mutations) {
      // check added nodes
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue

        // check the node itself
        if (node.hasAttribute?.(`data-tooltip`)) targets.add(node)

        // check descendants
        node
          .querySelectorAll?.(`[data-tooltip]`)
          .forEach((el) => targets.add(el))
      }

      // check attribute changes on existing elements
      if (
        mutation.type === `attributes` &&
        mutation.attributeName === `data-tooltip` &&
        mutation.target.hasAttribute(`data-tooltip`)
      )
        targets.add(mutation.target)
    }

    // handle all collected targets
    targets.forEach((target) => new Tooltip(target).init())
  })

  observer.observe(document.body, {
    childList: true, // watch for added/removed nodes
    subtree: true, // observe all descendants
    attributes: true, // watch for attribute changes
    attributeFilter: [`data-tooltip`], // only care about this attribute
  })
}
